import React from "react";
import BaseCard from "../../../components/ui/BaseCard";
import BaseCheckbox from "../../../components/ui/BaseCheckbox";
import { useFormik } from "formik";
import useAuth from "../../../hooks/useAuth";
import { useParams } from "react-router-dom";
import {
  createDesignationPermissions,
  fetchAllPermissionsByDesignation,
} from "../../../services/api/designation";
import { useMutation, useQuery } from "@tanstack/react-query";
import { ModuleType } from "../../../utils/enum";
import { getDesignationPermissionsByModule } from "../../../utils/getDesignationPermissionsByModule";

const WorkForce = ({
  designationPermissions,
  designationId,
  refetchAllPermissionsByDesignation,
}) => {
  const workForceEngineersPermissions = getDesignationPermissionsByModule(
    designationPermissions,
    ModuleType.WORK_FORCE_ENGINEERS
  );

  const talentPoolPermissions = getDesignationPermissionsByModule(
    designationPermissions,
    ModuleType.WORK_FORCE_TALENT_POOL
  );

  const coverageMapPermissions = getDesignationPermissionsByModule(
    designationPermissions,
    ModuleType.WORK_FORCE_COVERAGE_MAP
  );

  const {
    isPending: isCreateDesignationPermissionsApiLoading,
    error: isCreateDesignationPermissionsApiError,
    mutateAsync: createDesignationPermissionsMutation,
  } = useMutation({
    mutationFn: createDesignationPermissions,
    enabled: false,
  });

  const formik = useFormik({
    initialValues: {
      WORK_FORCE_ENGINEERS: {
        isRead: workForceEngineersPermissions.hasReadAccess,
        isWrite: workForceEngineersPermissions.hasWriteAccess,
        isUpdate: workForceEngineersPermissions.hasUpdateAccess,
        isDelete: workForceEngineersPermissions.hasDeleteAccess,
      },
      WORK_FORCE_TALENT_POOL: {
        isRead: talentPoolPermissions.hasReadAccess,
        isWrite: talentPoolPermissions.hasWriteAccess,
        isUpdate: talentPoolPermissions.hasUpdateAccess,
        isDelete: talentPoolPermissions.hasDeleteAccess,
      },
      WORK_FORCE_COVERAGE_MAP: {
        isRead: coverageMapPermissions.hasReadAccess,
        isWrite: coverageMapPermissions.hasWriteAccess,
        isUpdate: coverageMapPermissions.hasUpdateAccess,
        isDelete: coverageMapPermissions.hasDeleteAccess,
      },
    },
    onSubmit: (values) => {
      // Replace with your actual submit handler
      // handleConfirmApprovalButton(values);
    },
    validateOnChange: true,
    validateOnBlur: true,
    enableReinitialize: true,
    validateOnMount: true,
  });

  const handleTalentPoolPermissionsChange = async (
    e,
    value,
    module,
    permissionId
  ) => {
    try {
      formik.setFieldValue(e.target.name, e.target.checked);
      const payload = {
        ...formik.values.WORK_FORCE_TALENT_POOL,
        [value]: e.target.checked,
        module,
        designationId: +designationId,
        permissionId,
      };

      await createDesignationPermissionsMutation(payload);
      await refetchAllPermissionsByDesignation();
    } catch (error) {}
  };
  const handleWorkForceEngineerPermissionsChange = async (
    e,
    value,
    module,
    permissionId
  ) => {
    try {
      formik.setFieldValue(e.target.name, e.target.checked);
      const payload = {
        ...formik.values.WORK_FORCE_ENGINEERS,
        [value]: e.target.checked,
        module,
        designationId: +designationId,
        permissionId,
      };

      await createDesignationPermissionsMutation(payload);
      await refetchAllPermissionsByDesignation();
    } catch (error) {}
  };

  const handleCoverageMapPermissionsChange = async (
    e,
    value,
    module,
    permissionId
  ) => {
    try {
      formik.setFieldValue(e.target.name, e.target.checked);
      const payload = {
        ...formik.values.WORK_FORCE_COVERAGE_MAP,
        [value]: e.target.checked,
        module,
        designationId: +designationId,
        permissionId,
      };

      await createDesignationPermissionsMutation(payload);
      await refetchAllPermissionsByDesignation();
    } catch (error) {}
  };

  return (
    <BaseCard elevation={0} className="p-5 rounded-lg">
      <div className="border-b pb-4">
        <p className="text-dark text-base font-normal mb-2">
          Work Force Engineers
        </p>
        <div className="grid grid-cols-6">
          <BaseCheckbox
            label="Read All Engineers"
            name="WORK_FORCE_ENGINEERS.isRead"
            checked={formik.values.WORK_FORCE_ENGINEERS.isRead}
            onChange={(e) =>
              handleWorkForceEngineerPermissionsChange(
                e,
                "isRead",
                ModuleType.WORK_FORCE_ENGINEERS,
                workForceEngineersPermissions.permissionId
              )
            }
          />
          <BaseCheckbox
            label="Add Engineer in Talent Pool"
            name="WORK_FORCE_ENGINEERS.isUpdate"
            checked={formik.values.WORK_FORCE_ENGINEERS.isUpdate}
            onChange={(e) =>
              handleWorkForceEngineerPermissionsChange(
                e,
                "isUpdate",
                ModuleType.WORK_FORCE_ENGINEERS,
                workForceEngineersPermissions.permissionId
              )
            }
          />
        </div>
      </div>

      <div className="border-b pb-4">
        <p className="text-dark text-base font-normal mb-2">Talent Pool</p>
        <div className="grid grid-cols-7">
          <BaseCheckbox
            label="Read Talent Pool"
            name="WORK_FORCE_TALENT_POOL.isRead"
            checked={formik.values.WORK_FORCE_TALENT_POOL.isRead}
            onChange={(e) =>
              handleTalentPoolPermissionsChange(
                e,
                "isRead",
                ModuleType.WORK_FORCE_TALENT_POOL,
                talentPoolPermissions.permissionId
              )
            }
          />
          <BaseCheckbox
            label="Create Talent Pool"
            name="WORK_FORCE_TALENT_POOL.isWrite"
            checked={formik.values.WORK_FORCE_TALENT_POOL.isWrite}
            onChange={(e) =>
              handleTalentPoolPermissionsChange(
                e,
                "isWrite",
                ModuleType.WORK_FORCE_TALENT_POOL,
                talentPoolPermissions.permissionId
              )
            }
          />

          <BaseCheckbox
            label="Remove Engineer from Talent Pool"
            name="WORK_FORCE_TALENT_POOL.isDelete"
            checked={formik.values.WORK_FORCE_TALENT_POOL.isDelete}
            onChange={(e) =>
              handleTalentPoolPermissionsChange(
                e,
                "isDelete",
                ModuleType.WORK_FORCE_TALENT_POOL,
                talentPoolPermissions.permissionId
              )
            }
          />
        </div>
      </div>

      <div className="border-b pb-4">
        <p className="text-dark text-base font-normal mb-2">Coverage Map</p>
        <div className="grid grid-cols-6">
          <BaseCheckbox
            label="Read Coverage Map"
            name="WORK_FORCE_COVERAGE_MAP.isRead"
            checked={formik.values.WORK_FORCE_COVERAGE_MAP.isRead}
            onChange={(e) =>
              handleCoverageMapPermissionsChange(
                e,
                "isRead",
                ModuleType.WORK_FORCE_COVERAGE_MAP,
                coverageMapPermissions.permissionId
              )
            }
          />
        </div>
      </div>
    </BaseCard>
  );
};

export default WorkForce;
